import { Component, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PublicationService } from '../../../services/publication.service';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { AbstractDetailsComponent } from '../../../components/shared/abstract-details.component';
import { NgForm } from '@angular/forms';
import { SapiService, SapiEntity } from '../../../services/sapi.service';
import { BbTableService, TableRow } from '../../../services/bb-table.service';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AdBlockDetectorService } from '../../../services/ad-block-detector.service';
import { DateRange } from '../../../interfaces/dateRange';
import { Adunit } from '../adunit/adunit-list.component';
import { AdunitStats } from '../../../shared/models/sapi/adunit-stats.model';
import { AdunitStatsService } from '../../../services/sapi/adunit-stats.service';

import dayjs from 'dayjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { LineItem } from '../lineitem/lineitem-details.component';
import { TabItem } from '../../shared/bb-page-header/bb-page-header.component';
import { LocationService } from '../../../services/location.service';
import { ClipboardService } from 'ngx-clipboard';
import { FilterSet, FilterSetService } from '../../../services/filter-set.service';
import { AdServicesService } from '../../../services/ad-services.service';
import { DefaultService } from '../../../services/default.service';
import { Playout } from '../../../interfaces/playout';
import { StatisticsService } from '../../../services/statistics.service';

interface StatsObject {
	start?: Date;
	compare_start?: Date;
	end?: Date;
	compare_end?: Date;
	adunit?: string;
}

@Component({
	selector: 'adunit-details',
	templateUrl: './adunit-details.component.html',
	styleUrls: ['./adunit-details.component.scss'],
	providers: [BbTableService, FilterSetService]
})

export class AdunitDetailsComponent extends AbstractDetailsComponent {
	@ViewChild('formRef', { static: true }) form: NgForm;

	adBlocker: boolean;

	statsObject: StatsObject;
	statsDate: DateRange;

	lineitemStats: AdunitStats[][];
	rows: TableRow[];

	tabItems: TabItem[];

	lineitem: LineItem;
	lineitemSaveAction: any;
	assignedLineitems: string[];
	lineitemFilterSet: FilterSet;

	defaultLineItem: LineItem;
	initialPlayoutCode: string;

	outstream: boolean;

	placeholder: string;
	adunitDefault: any;
	preview: boolean;

	adunit: Adunit;
	showPrebid = false;

	protected markedDuplicate = false;

	private loadStatsOnAdunitLoad = false;

	constructor(protected route: ActivatedRoute,
		protected location: Location,
		protected locationService: LocationService,
		protected publicationService: PublicationService,
		protected sapiService: SapiService,
		protected customFieldsService: CustomFieldsService,
		protected bbTableService: BbTableService,
		protected changeDetectorRef: ChangeDetectorRef,
		protected adBlockDetectorService: AdBlockDetectorService,
		protected adunitStatsService: AdunitStatsService,
		protected translate: TranslateService,
		protected defaultService: DefaultService,
		public adServicesService: AdServicesService,
		protected statisticsService: StatisticsService,
		protected clipboardService: ClipboardService) {

		super(route, location);

		this.adBlockDetectorService.adBlocker.subscribe((adBlocker: boolean) => {
			this.adBlocker = adBlocker;
		});

		this.lineitemStats = [];
		this.assignedLineitems = [];
		this.lineitemFilterSet = [{
			filters: [{
				type: 'lineitem',
				field: 'isVMAP',
				operator: 'is',
				value: 'false'
			}]
		}];

		this.placeholder = this.translate.instant('ASSIGN_LINE_ITEM_OR_AUCTION');
		this.adunitDefault = require('../../../resources/defaults/adunit.json');

		this.entityConfiguration = {
			name: 'adunit',
			parentPath: '/adservices/adunits',
			basePath: '/adservices/adunits/',
			entityTranslation: 'adunit',
			noReloadAfterSave: true,
			defaults: this.adunitDefault
		};

		this.defaultLineItem = {
			timeout: 10,
			status: 'draft',
			playout: {
				commercialBehaviour: '',
				disableControls: 'false',
				imaCustomPlaybackForIOS10Plus: '',
				skipButtonText: 'Skip ad',
				skipCounterText: 'You can skip this ad in '
			}
		};

		this.entityLoaded.pipe(first(_ => !this.entityLoading)).subscribe(() => {
			this.rows = [];

			this.adunit = this.entity as Adunit;

			if (!this.adunit.playout) {
				this.adunit.playout = {
					label: '',
					name: '',
					main: undefined
				};
			}

			if (this.adunit.positionType === 'inarticle') {
				this.outstream = true;
			} else {
				this.outstream = false;
			}

			if (this.isNew) {
				this.form.form.markAsDirty();
				this.adunit.positionType = this.activeId.replace('new-', '');
				this.adunit = { ...this.adunit };

				if (this.entityIsDuplicate && !this.markedDuplicate) {
					this.markedDuplicate = true;

					this.adunit.assignedLineitems = [];
					this.adunit.lineitems = [];
				} else {
					this.adunit.playout = this.defaultService.getAdunitPlayoutDefault(this.adunit.positionType);
				}


				if (this.adunit.positionType === 'vmap') {
					this.lineitem = { ...this.defaultLineItem };
					this.lineitem.isVMAP = true;
					this.lineitem.status = 'published';
					this.lineitem.playout.commercialBehaviour = 'Once per load';

					if (this.statisticsService.data && this.statisticsService.data.playoutCode) {
						this.adunit.playoutCode = this.statisticsService.data.playoutCode;
					}

					this.adunit.lineitems = [this.lineitem];
					const row: TableRow = { id: this.lineitem.id, data: this.lineitem };
					this.rows.push(row);
				}
				this.entity = this.adunit as SapiEntity;
			} else {
				this.initialPlayoutCode = this.adunit.playoutCode;
				if (this.adunit && this.adunit.lineitems && this.adunit.lineitems.length) {
					if (this.adunit.positionType === 'vmap') {
						this.lineitem = this.adunit.lineitems[0];
					} else {
						this.lineitem = null;
					}

					if (this.statsObject) {
						this.statsObject.adunit = this.adunit.code;
					} else {
						this.statsObject = { adunit: this.adunit.code };
					}

					this.adunit.lineitems.forEach((lineitem: LineItem) => {
						const row: TableRow = { id: lineitem.id, data: lineitem };
						this.rows.push(row);
						this.getLineitemStats(lineitem);
					});
				}
			}
			this.statisticsService.data = {};
		});

		this.subscriptions.add(this.entityLoaded.subscribe(() => {
			if (this.adunit?.lineitems?.length !== undefined) {
				this.updateTabs();
			}
		}));

	}

	updateTabs(forceShow = false) {
		if (!forceShow) {
			this.showPrebid = false;
			this.adunit.lineitems.forEach((lineitem: LineItem) => {
				this.showPrebid = this.showPrebid || lineitem.usePrebid === 'true';
			});
		} else {
			this.showPrebid = true;
		}

		const tabItems = [
			{
				title: this.translate.instant('GENERAL'),
				routerLink: ['/adservices/adunits', 'general'],
				icon: 'tags',
			},
			{
				title: this.translate.instant('PREBID'),
				routerLink: ['/adservices/adunits', 'prebid'],
				icon: 'gavel',
			}
		];

		if (this.showPrebid) {
			this.tabItems = tabItems;
		} else {
			this.tabItems = [];
		}
	}

	rowChanged(index: number, row: TableRow) {
		this.rows[index].data = row.data;
	}

	onItemRemoved(index: number) {
		if (this.rows) {
			this.rows.splice(index, 1);
			this.form.form.markAsDirty();
		}
	}

	createNewLineitem() {
		const lineitem: LineItem = { ...this.defaultLineItem };
		if (!this.isNew) {
			lineitem.relatedAdunits = [];
			lineitem.relatedAdunits.push(this.entity.id);
		}
		this.rows.push({ id: null, quickEditing: true, data: lineitem });
		this.form.form.markAsDirty();
	}

	lineitemDragged(event: CdkDragDrop<string[]>) {
		if (event.currentIndex === event.previousIndex) {
			return;
		}
		const lineitemRow: TableRow = this.rows[event.previousIndex];
		this.rows[event.previousIndex] = this.rows[event.currentIndex];
		this.rows[event.currentIndex] = lineitemRow;
		this.form.form.markAsDirty();
	}

	getLineitemStats(lineitem: LineItem) {
		const lineitemParams: any = { ...this.statsObject };
		lineitemParams.lineitem = lineitem.code;

		this.adunitStatsService.get('', lineitemParams).subscribe((adunitStats: AdunitStats) => {
			this.lineitemStats[lineitem.id] = adunitStats;
		});
	}

	getAdunitStats() {
		this.statsObject.adunit = this.adunit.code;

		const adunit: Adunit = this.entity as Adunit;
		if (adunit && adunit.lineitems) {
			adunit.lineitems.forEach((lineitem: LineItem) => {
				this.getLineitemStats(lineitem);
			});
		}
		this.changeDetectorRef.detectChanges();
	}

	datesChanged(dateRange: DateRange) {
		setTimeout(() => {
			this.statsDate = dateRange;
			const statsParams: any = {
				start: this.statsDate.startDate.toISOString(),
				end: this.statsDate.endDate.toISOString(),
			};

			// when requesting stats < 1 day, compare with previous day same time period (other cases handled by backend)
			if (Math.abs(dayjs(statsParams.start).diff(statsParams.end, 'hours')) <= 24) {
				statsParams.compare_start = dayjs(statsParams.start).subtract(1, 'days').toISOString();
				statsParams.compare_end = dayjs(statsParams.end).subtract(1, 'days').toISOString();
			}

			this.statsObject = statsParams;

			if (this.adunit) {
				this.getAdunitStats();
			} else {
				this.loadStatsOnAdunitLoad = true;
			}
		});
	}

	lineitemSelected(lineitemId: string) {
		if (lineitemId) {
			if (this.rows.map((row: TableRow) => row.id).indexOf(lineitemId) < 0) {
				this.sapiService.get('lineitem', lineitemId).subscribe((lineitem: LineItem) => {
					if (!lineitem.relatedAdunits) {
						lineitem.relatedAdunits = [];
					}
					lineitem.relatedAdunits.push(this.entity.id);
					const row: TableRow = { id: lineitemId, data: lineitem };

					this.rows.push(row);
					this.getLineitemStats(lineitem);
					if (lineitem.usePrebid === 'true') {
						this.updateTabs(true);
					}
				});
				this.form.form.markAsDirty();
			}
			this.assignedLineitems = this.assignedLineitems.filter(() => false);
		}
	}

	goToLineitem(row: TableRow) {
		if (row && row.id) {
			this.router.navigate(['/adservices/lineitems', row.id, 'settings']);
		}
	}

	updatePlayoutAdunit(playoutCode: string, adunit: Adunit) {
		this.sapiService.get('playout', playoutCode).subscribe((sapiEntity: SapiEntity) => {
			const playout: Playout = sapiEntity as Playout;
			if (!playout.adunits) {
				playout.adunits = [];
			}

			let currentAdunit: Adunit;
			let removeAdunitPlayouts: Adunit[];

			switch ((this.entity as Adunit).positionType) {
				case 'preroll':
					removeAdunitPlayouts = [...playout.adunitsPreroll];
					playout.adunitsPreroll = [];
					playout.adunitsPreroll[0] = adunit;
					break;
				case 'postroll':
					removeAdunitPlayouts = [...playout.adunitsPostroll];
					playout.adunitsPostroll = [];
					playout.adunitsPostroll[0] = adunit;
					break;
				case 'vmap':
					removeAdunitPlayouts = [...playout.adunitsVMAP];
					playout.adunitsVMAP = [];
					playout.adunitsVMAP[0] = adunit;
					break;
				case 'overlay':
					removeAdunitPlayouts = playout.adunits.filter((ad: Adunit) => ad.positionType === 'overlay');
					currentAdunit = this.findOverlayAd(playout);
					break;
			}

			if (currentAdunit) {
				for (let i = 0; i < playout.adunits.length; i++) {
					if (playout.adunits[i].id === currentAdunit.id) {
						if (adunit) {
							playout.adunits[i] = adunit;
						} else {
							delete playout.adunits[i];
						}
						break;
					}
				}
			} else {
				if (adunit) {
					playout.adunits.push(adunit);
				}
			}

			if (removeAdunitPlayouts) {
				if (adunit) {
					removeAdunitPlayouts = removeAdunitPlayouts.filter((ad: Adunit) => ad.id !== adunit.id);
				}

				removeAdunitPlayouts.forEach((ad: Adunit) => {
					this.sapiService.get('adunit', ad.id).subscribe((adEntity: SapiEntity) => {
						const adunitWithoutPlayoutcode: Adunit = adEntity as Adunit;
						adunitWithoutPlayoutcode.playoutCode = '';
						this.sapiService.update('adunit', adunitWithoutPlayoutcode).subscribe();
					});
				});
			}
			this.sapiService.update('playout', playout).subscribe();
		});
	}

	findOverlayAd(playout: Playout): Adunit {
		let overlayAdunit: Adunit = null;

		if (playout) {
			const adunitIds: string[] = [];
			if (playout.adunits.length) {
				['Preroll', 'Postroll', 'VMAP'].forEach((adunitType: string) => {
					if (playout['adunits' + adunitType] && playout['adunits' + adunitType].length) {
						adunitIds.push(playout['adunits' + adunitType][0].id);
					}
				});
				const adunits = playout.adunits.filter((adunit: Adunit) => adunitIds.indexOf(adunit.id) < 0);
				if (adunits && adunits.length) {
					overlayAdunit = adunits[0];
				}
			}
		}
		return overlayAdunit;
	}

	cancel(): Subject<SapiEntity> {
		const cancelSubject = super.cancel();
		this.rows = [];
		this.adunit.lineitems?.forEach((lineitem: LineItem) => {
			const row: TableRow = { id: lineitem.id, data: lineitem };
			this.rows.push(row);
			this.getLineitemStats(lineitem);
		});
		this.formGroup.markAsPristine();
		return cancelSubject;
	}

	save(force?: boolean, extraHeaders?: any, saveDirectly: boolean = false, publish?: boolean): Observable<any> {
		let adunit: Adunit = this.entity as Adunit;
		let newLineitem = false;
		this.rows.forEach((row: TableRow) => {
			row.quickEditing = false;
		});

		adunit.lineitems = this.rows.map((row: TableRow) => {
			if (!row.id) {
				newLineitem = true;
			}
			return row.data;
		});

		adunit.assignedLineitems = adunit.lineitems.map((lineitem: LineItem) => lineitem.id);

		const saveObservable = super.save(force, extraHeaders, saveDirectly, publish);

		saveObservable.subscribe(() => {
			if (this.isNew) {
				this.outstream = adunit.positionType === 'inarticle';
				this.form.form.markAsPristine();
				this.router.navigate([this.entityConfiguration.basePath, this.entity.id, 'general']);
			} else if (newLineitem) {
				adunit = this.entity as Adunit;
				this.rows.forEach((lineitem: TableRow) => {
					if (!lineitem.id) {
						if (adunit.lineitems && adunit.lineitems.length) {
							adunit.lineitems.forEach((updatedLineitem: LineItem) => {
								if (lineitem.data.code === updatedLineitem.code) {
									lineitem.id = updatedLineitem.id;
									lineitem.data.id = updatedLineitem.id;
									this.getLineitemStats(lineitem.data);
								}
							});
						}
					}
				});

				this.formGroup.markAsPristine();
			}
		});

		return saveObservable;
	}

	load(id: string, duplicating?: boolean): Observable<SapiEntity> {

		const loadObservable = super.load(id, duplicating);

		loadObservable.subscribe((entity) => {
			if (this.loadStatsOnAdunitLoad) {
				this.getAdunitStats();
				this.loadStatsOnAdunitLoad = false;
			}
		});

		return loadObservable;
	}
}
